import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import apiService from '../services/apiService';
import UserInsightsComponent from './UserInsightsComponent';
import UserDormantAnalyticsModal from './UserDormantAnalyticsModal';
import UserDetailPopup from './graphrag/components/UserDetailPopup';
import graphragService from './graphrag/services/graphragService';
import { useUserMetadata } from '../../../context/UserMetadataContext';

/**
 * UsersListComponent - Test component for Profile360 REST API
 * Demonstrates listing user profiles from the FastAPI REST API
 * Click on a user to view their insights
 */
const UsersListComponent = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { userMetadata } = useUserMetadata();
  const tenantName = userMetadata?.company || 'trustx';

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState(null);

  // Dormant Analytics Modal State
  const [dormantAnalyticsUser, setDormantAnalyticsUser] = useState(null);
  const [showDormantAnalytics, setShowDormantAnalytics] = useState(false);

  // User Detail Popup State (GraphRAG-style popup)
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [popupUser, setPopupUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
          scope: "openid profile email"
        }
      });

      const response = await apiService.getAllUsers(token);

      // Extract users array from response
      if (response.success && response.data) {
        const userData = Array.isArray(response.data) ? response.data : [];
        setUsers(userData);
        setFilteredUsers(userData);
      } else {
        setUsers([]);
        setFilteredUsers([]);
        throw new Error(response.error || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to fetch users');
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = users.filter(user => {
      return (
        (user.display_name || '').toLowerCase().includes(term) ||
        (user.email || '').toLowerCase().includes(term) ||
        (user.department || '').toLowerCase().includes(term) ||
        (user.job_title || '').toLowerCase().includes(term)
      );
    });
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setLoadingInsights(true);
    setInsightsError(null);
    setInsightsData(null);

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
          scope: "openid profile email"
        }
      });

      // Set auth token and tenant for apiService
      apiService.setAuthToken(token);

      // Fetch insights for the user
      const response = await apiService.getUserInsights(user.email);

      if (response.success && response.data) {
        setInsightsData(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch insights');
      }
    } catch (err) {
      console.error('Error fetching insights:', err);
      setInsightsError(err.message || 'Failed to load insights');
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleCloseInsights = () => {
    setSelectedUser(null);
    setInsightsData(null);
    setInsightsError(null);
  };

  const handleOpenDormantAnalytics = (user, event) => {
    // Prevent row click event from triggering if event passed (from button)
    if (event) {
      event.stopPropagation();
    }
    setDormantAnalyticsUser(user);
    setShowDormantAnalytics(true);
  };

  const handleUserRowClick = (user) => {
    // Open dormant analytics when clicking on row
    handleOpenDormantAnalytics(user);
  };

  const handleCloseDormantAnalytics = () => {
    setShowDormantAnalytics(false);
    setDormantAnalyticsUser(null);
  };

  // Handle double-click to open UserDetailPopup (GraphRAG-style)
  const handleUserDoubleClick = (user) => {
    // Transform user data to match the format expected by UserDetailPopup
    const popupUserData = {
      email: user.email,
      name: user.display_name || user.email,
      displayName: user.display_name || user.email,
      department: user.department,
      jobTitle: user.job_title,
      type: 'User',
      entity_type: 'User'
    };
    setPopupUser(popupUserData);
    setShowUserPopup(true);
  };

  const handleCloseUserPopup = () => {
    setShowUserPopup(false);
    setPopupUser(null);
  };

  const handleExploreUser = (user) => {
    // Optional: Navigate to GraphRAG with user query
    console.log('Explore user:', user);
    handleCloseUserPopup();
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading user profiles from REST API...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            REST API Error
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        <Button variant="outlined" onClick={handleRefresh}>
          Retry
        </Button>
      </Box>
    );
  }

  const totalCount = users.length;
  const displayedCount = filteredUsers.length;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <GroupIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={700}>
            User Profiles
          </Typography>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          size="small"
        >
          Refresh
        </Button>
      </Stack>

      {/* Search Filter */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search users by name, email, department, or title..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Stats */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing{' '}
          <strong>{displayedCount}</strong> of <strong>{totalCount}</strong> users
        </Typography>
        {searchTerm && (
          <Chip
            label="filtered"
            size="small"
            variant="outlined"
            onDelete={() => setSearchTerm('')}
          />
        )}
      </Stack>

      {filteredUsers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          {searchTerm ? (
            <>
              <Typography color="text.secondary">
                No users found matching "{searchTerm}"
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Try a different search term
              </Typography>
            </>
          ) : (
            <>
              <Typography color="text.secondary">No user profiles found</Typography>
              <Typography variant="caption" color="text.disabled">
                Try adding some test data to the database
              </Typography>
            </>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Job Title</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell width={60}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => handleUserRowClick(user)}
                  onDoubleClick={() => handleUserDoubleClick(user)}
                  sx={{ cursor: 'pointer' }}
                  title="Click: Dormant Analytics | Double-click: Full Profile"
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {user.display_name || user.email || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {user.department || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {user.job_title || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {user.updated_at
                        ? new Date(user.updated_at).toLocaleDateString()
                        : user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Dormant Analytics">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => handleOpenDormantAnalytics(user, e)}
                      >
                        <PersonOffIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* User Insights Dialog */}
      <Dialog
        open={!!selectedUser}
        onClose={handleCloseInsights}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '70vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" fontWeight={700}>
                User Insights &amp; Risk Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser?.display_name || selectedUser?.email}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseInsights} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loadingInsights && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading insights...
              </Typography>
            </Box>
          )}

          {insightsError && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="error" gutterBottom>
                Failed to load insights
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {insightsError}
              </Typography>
              <Button
                variant="contained"
                onClick={() => handleUserClick(selectedUser)}
                sx={{ mt: 2 }}
              >
                Retry
              </Button>
            </Box>
          )}

          {insightsData && !loadingInsights && (
            <UserInsightsComponent insightsData={insightsData} />
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCloseInsights}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dormant Analytics Modal */}
      {dormantAnalyticsUser && (
        <UserDormantAnalyticsModal
          open={showDormantAnalytics}
          onClose={handleCloseDormantAnalytics}
          user={dormantAnalyticsUser}
          defaultThreshold={30}
        />
      )}

      {/* User Detail Popup (GraphRAG-style) - Opens on double-click */}
      {showUserPopup && popupUser && (
        <UserDetailPopup
          user={popupUser}
          onClose={handleCloseUserPopup}
          onExplore={handleExploreUser}
          graphragService={graphragService}
          tenantId={tenantName}
        />
      )}
    </Box>
  );
};

export default UsersListComponent;
